import {
	ArrowLeftRight,
	BarChart3,
	BookOpen,
	Building2,
	ChevronDown,
	Clock,
	Database,
	FileText,
	GitCompare,
	GitMerge,
	Grid3X3,
	Landmark,
	LayoutDashboard,
	ListTodo,
	PanelLeftClose,
	PanelLeftOpen,
	RefreshCw,
	Settings,
	Shield,
	SlidersHorizontal,
	Sparkles,
	TrendingUp,
	Users,
	Wrench,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTerminology } from "@/hooks/useTerminology";
import { useActions } from "../../contexts/ActionsContext";
import { useApp } from "../../contexts/AppContext";
import { useRecommendations } from "../../contexts/RecommendationsContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useMode } from "@/providers/ModeProvider";
import { cn } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ScrollArea } from "../ui/scroll-area";
import {
	Sidebar as ShadcnSidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarSeparator,
	useSidebar,
} from "../ui/sidebar";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavChild {
	icon: React.ReactNode;
	label: string;
	pageKey: string;
	permission?: string;
	demoOnly?: boolean;
}

interface NavItemDef {
	icon: React.ReactNode;
	label: string;
	pageKey?: string;
	permission?: string;
	badge?: number;
	demoOnly?: boolean;
	children?: NavChild[];
}

// ─── Collapse toggle button wired to sidebar context ─────────────────────────

const CollapseToggle: React.FC = () => {
	const { toggleSidebar, open } = useSidebar();
	return (
		<Button
			variant="ghost"
			size="icon"
			onClick={toggleSidebar}
			className="h-7 w-7 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent"
			title={open ? "Collapse sidebar" : "Expand sidebar"}
		>
			{open ? (
				<PanelLeftClose className="h-[18px] w-[18px]" />
			) : (
				<PanelLeftOpen className="h-[18px] w-[18px]" />
			)}
		</Button>
	);
};

// ─── Inner sidebar that consumes useSidebar ───────────────────────────────────

interface InnerSidebarProps {
	currentPage: string;
	onNavigate: (page: string) => void;
	navItems: NavItemDef[];
}

const InnerSidebar: React.FC<InnerSidebarProps> = ({
	currentPage,
	onNavigate,
	navItems,
}) => {
	const { open } = useSidebar();

	// Track manually toggled group — null means "follow currentPage", a number means "user opened this group"
	const [manualOpenIdx, setManualOpenIdx] = useState<number | null>(null);

	// The active group index derived from currentPage
	const activeGroupIdx = navItems.findIndex((item) =>
		item.children?.some((c) => c.pageKey === currentPage),
	);

	// When currentPage changes to a different group (or no group), reset manual override
	useEffect(() => {
		setManualOpenIdx(null);
	}, [currentPage]);

	// The open group is: manual override if set, otherwise the active group
	const openGroupIdx =
		manualOpenIdx !== null
			? manualOpenIdx
			: activeGroupIdx >= 0
				? activeGroupIdx
				: null;

	const handleGroupToggle = (idx: number) => {
		setManualOpenIdx((prev) => (prev === idx ? null : idx));
	};

	return (
		<ShadcnSidebar
			variant="floating"
			collapsible="icon"
			className="top-0 h-svh"
		>
			{/* ── Header ── */}
			<SidebarHeader className="px-3 pt-3 pb-2">
				<div className="flex items-center gap-2">
					<CollapseToggle />
					{open && (
						<span className="text-sm font-semibold text-sidebar-foreground truncate">
							Cash Forecaster
						</span>
					)}
				</div>
			</SidebarHeader>

			<SidebarSeparator />

			{/* ── Nav ── */}
			<SidebarContent>
				<ScrollArea className="flex-1">
					<SidebarGroup>
						<SidebarGroupContent>
							<SidebarMenu>
								{navItems.map((item, idx) => {
									if (item.children && item.children.length > 0) {
										// Collapsible parent group
										const isGroupActive = item.children.some(
											(c) => c.pageKey === currentPage,
										);
										return (
											<CollapsibleNavGroup
												key={idx}
												item={item}
												currentPage={currentPage}
												onNavigate={onNavigate}
												isGroupActive={isGroupActive}
												isSidebarOpen={open}
												isOpen={openGroupIdx === idx}
												anyGroupOpen={openGroupIdx !== null}
												onToggle={() => handleGroupToggle(idx)}
											/>
										);
									}

									// Leaf item
									const isActive = item.pageKey === currentPage;
									return (
										<SidebarMenuItem key={idx}>
											<SidebarMenuButton
												isActive={isActive}
												tooltip={item.label}
												onClick={() => item.pageKey && onNavigate(item.pageKey)}
											>
												{item.icon}
												<span>{item.label}</span>
												{item.badge !== undefined && item.badge > 0 && open && (
													<Badge
														variant="secondary"
														className="ml-auto text-[10px] px-1.5 py-0 h-4 min-w-[1rem] flex items-center justify-center"
													>
														{item.badge}
													</Badge>
												)}
											</SidebarMenuButton>
										</SidebarMenuItem>
									);
								})}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				</ScrollArea>
			</SidebarContent>

			{/* ── Footer ── */}
			<SidebarFooter className="pb-3" />
		</ShadcnSidebar>
	);
};

// ─── Collapsible nav group ────────────────────────────────────────────────────

interface CollapsibleNavGroupProps {
	item: NavItemDef;
	currentPage: string;
	onNavigate: (page: string) => void;
	isGroupActive: boolean;
	isSidebarOpen: boolean;
	isOpen: boolean;
	anyGroupOpen: boolean;
	onToggle: () => void;
}

const CollapsibleNavGroup: React.FC<CollapsibleNavGroupProps> = ({
	item,
	currentPage,
	onNavigate,
	isGroupActive,
	isSidebarOpen,
	isOpen,
	anyGroupOpen,
	onToggle,
}) => {
	// Collapsed (icon) sidebar: the inline Collapsible content is hidden, so
	// surface the child items in a right-anchored flyout menu instead.
	if (!isSidebarOpen) {
		return (
			<SidebarMenuItem>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<SidebarMenuButton
							tooltip={item.label}
							isActive={isGroupActive}
							className="w-full"
						>
							{item.icon}
							<span className="flex-1 text-left">{item.label}</span>
						</SidebarMenuButton>
					</DropdownMenuTrigger>
					<DropdownMenuContent side="right" align="start" className="min-w-48">
						<DropdownMenuLabel>{item.label}</DropdownMenuLabel>
						{item.children?.map((child, cidx) => {
							const isActive = child.pageKey === currentPage;
							return (
								<DropdownMenuItem
									key={cidx}
									onClick={() => onNavigate(child.pageKey)}
									className={cn(
										isActive &&
											"bg-sidebar-accent text-sidebar-accent-foreground font-medium",
									)}
								>
									{child.label}
								</DropdownMenuItem>
							);
						})}
					</DropdownMenuContent>
				</DropdownMenu>
			</SidebarMenuItem>
		);
	}

	return (
		<Collapsible open={isOpen} onOpenChange={onToggle}>
			<SidebarMenuItem>
				<CollapsibleTrigger asChild>
					<SidebarMenuButton
						tooltip={item.label}
						isActive={isGroupActive && !anyGroupOpen}
						className="w-full"
					>
						{item.icon}
						<span className="flex-1 text-left">{item.label}</span>
						{isSidebarOpen && (
							<ChevronDown
								className={cn(
									"h-4 w-4 flex-shrink-0 transition-transform duration-200",
									isOpen && "rotate-180",
								)}
							/>
						)}
					</SidebarMenuButton>
				</CollapsibleTrigger>
			</SidebarMenuItem>

			<CollapsibleContent>
				<SidebarMenu className="ml-4 border-l border-sidebar-border pl-2 pr-4 py-0.5 space-y-0.5">
					{item.children?.map((child, cidx) => {
						const isActive = child.pageKey === currentPage;
						return (
							<SidebarMenuItem key={cidx}>
								<SidebarMenuButton
									isActive={isActive}
									tooltip={child.label}
									onClick={() => onNavigate(child.pageKey)}
									size="default"
									className="rounded-md"
								>
									<span>{child.label}</span>
								</SidebarMenuButton>
							</SidebarMenuItem>
						);
					})}
				</SidebarMenu>
			</CollapsibleContent>
		</Collapsible>
	);
};

// ─── Public Sidebar component ─────────────────────────────────────────────────

interface SidebarProps {
	currentPage: string;
	onNavigate: (page: string) => void;
	onWidthChange?: (width: number) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
	currentPage,
	onNavigate,
}) => {
	const { hasPermission, isSystemAdmin } = usePermissions();
	const { currentUser } = useApp();
	const { isDemo } = useMode();
	const { myOpenActionCount } = useActions();
	const { openInsightCount } = useRecommendations();
	const { getForecastTerm, getScenarioTerm, getVarianceTerm, getActionsTerm } = useTerminology();

	// ── Nav item definitions ──────────────────────────────────────────────────

	const allNavItems: NavItemDef[] = [
		{
			icon: <LayoutDashboard className="h-4 w-4" />,
			label: "Dashboard",
			pageKey: "dashboard",
		},
		{
			icon: <TrendingUp className="h-4 w-4" />,
			label: `${getForecastTerm(true)}s`,
			permission: "view_forecasts",
			children: [
				{
					icon: <TrendingUp className="h-4 w-4" />,
					label: "90-Day View",
					pageKey: "forecast-90",
					permission: "view_90day_forecast_screen",
				},
				{
					icon: <TrendingUp className="h-4 w-4" />,
					label: "12-Month View",
					pageKey: "forecast-12m",
					permission: "view_12month_forecast_screen",
				},
				{
					icon: <TrendingUp className="h-4 w-4" />,
					label: "3-Year View",
					pageKey: "forecast-3y",
					permission: "view_3year_forecast_screen",
					demoOnly: true,
				},
				{
					icon: <RefreshCw className="h-4 w-4" />,
					label: `Refresh ${getForecastTerm(true)}`,
					pageKey: "forecast-refresh",
					permission: "trigger_forecast_refresh",
				},
			],
		},
		{
			icon: <ArrowLeftRight className="h-4 w-4" />,
			label: "Transactions",
			permission: "view_transactions",
			children: [
				{
					icon: <ArrowLeftRight className="h-4 w-4" />,
					label: "Synced Transactions",
					pageKey: "transactions",
				},
				{
					icon: <Clock className="h-4 w-4" />,
					label: "Recurring Transactions",
					pageKey: "recurring-transactions",
					permission: "manage_recurring_transactions",
				},
			],
		},
		{
			icon: <GitCompare className="h-4 w-4" />,
			label: getScenarioTerm(),
			pageKey: "scenarios",
			permission: "view_scenarios",
		},
		{
			icon: <BarChart3 className="h-4 w-4" />,
			label: `${getVarianceTerm()} Analysis`,
			pageKey: "variance",
			permission: "view_variance_analysis_screen",
			demoOnly: true,
		},
		{
			icon: <Sparkles className="h-4 w-4" />,
			label: "AI Recommendations",
			pageKey: "recommendations",
			badge: openInsightCount > 0 ? openInsightCount : undefined,
			permission: "view_operational_insights,view_strategic_insights",
			demoOnly: true,
		},
		{
			icon: <Users className="h-4 w-4" />,
			label: "Users",
			children: [
				{
					icon: <Users className="h-4 w-4" />,
					label: "User Management",
					pageKey: "admin-users",
					permission: "manage_users",
				},
				{
					icon: <Shield className="h-4 w-4" />,
					label: "Roles & Permissions",
					pageKey: "admin-roles-permissions",
					permission: "manage_roles_permissions",
				},
				{
					icon: <GitMerge className="h-4 w-4" />,
					label: "Approval Workflows",
					pageKey: "admin-approval-workflows",
					permission: "manage_approval_workflows",
				},
				{
					icon: <SlidersHorizontal className="h-4 w-4" />,
					label: "User Defaults",
					pageKey: "admin-user-defaults",
					permission: "manage_user_defaults",
					demoOnly: true,
				},
				{
					icon: <LayoutDashboard className="h-4 w-4" />,
					label: "Dashboard Layout",
					pageKey: "admin-dashboard-layout",
					permission: "manage_dashboard_layout",
				},
			],
		},
		{
			icon: <Settings className="h-4 w-4" />,
			label: "System Settings",
			children: [
				{
					icon: <Wrench className="h-4 w-4" />,
					label: "General Settings",
					pageKey: "admin-general-settings",
					permission: "manage_general_settings",
				},
				{
					icon: <BookOpen className="h-4 w-4" />,
					label: "Terminology",
					pageKey: "admin-terminology",
					permission: "manage_terminology",
				},
				{
					icon: <Database className="h-4 w-4" />,
					label: "Data Source Config",
					pageKey: "admin-data-sources",
					permission: "configure_data_sources",
				},
				{
					icon: <Grid3X3 className="h-4 w-4" />,
					label: "Parent Child Categories",
					pageKey: "admin-category-mapping",
					permission: "configure_data_sources",
				},
			],
		},
		{
			icon: <FileText className="h-4 w-4" />,
			label: "Reports",
			pageKey: "reports",
			permission: "view_reports,manage_report_permissions",
			demoOnly: true,
		},
		{
			icon: <ListTodo className="h-4 w-4" />,
			label: getActionsTerm(),
			pageKey: "actions",
			badge: myOpenActionCount > 0 ? myOpenActionCount : undefined,
		},
		{
			icon: <Landmark className="h-4 w-4" />,
			label: "Finance Settings",
			children: [
				{
					icon: <Building2 className="h-4 w-4" />,
					label: "Bank Accounts",
					pageKey: "bank-accounts",
					permission: "manage_bank_account_details",
				},
			],
		},
		{
			icon: <SlidersHorizontal className="h-4 w-4" />,
			label: "My Preferences",
			pageKey: "settings",
			demoOnly: true,
		},
	];

	// ── Permission filtering ──────────────────────────────────────────────────

	const checkPermission = (permission?: string): boolean => {
		if (!permission) return true;
		if (permission.includes(",")) {
			return permission
				.split(",")
				.map((p) => p.trim())
				.some((p) => hasPermission(p as never));
		}
		return hasPermission(permission as never);
	};

	const checkDemoOnly = (demoOnly?: boolean): boolean => {
		if (demoOnly && !isDemo) return false;
		return true;
	};

	const filterItems = (items: NavItemDef[]): NavItemDef[] =>
		items
			.map((item) => {
				if (!checkDemoOnly(item.demoOnly)) return null;
				if (item.children) {
					// Check parent permission first — if parent permission fails, hide entire group
					if (!checkPermission(item.permission)) return null;
					const filteredChildren = item.children.filter(
						(c) => checkPermission(c.permission) && checkDemoOnly(c.demoOnly),
					);
					if (filteredChildren.length === 0) return null;
					return { ...item, children: filteredChildren };
				}
				if (!checkPermission(item.permission)) return null;
				return item;
			})
			.filter((item): item is NavItemDef => item !== null);

	const navItems = filterItems(allNavItems);

	return (
		<InnerSidebar
			currentPage={currentPage}
			onNavigate={onNavigate}
			navItems={navItems}
		/>
	);
};
